import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import { MachineIcon, NotifyIcon } from 'web-components';
import { useTranslation } from 'react-i18next';
import { SENSOR_STATUS_OK } from '../../attrs/notifications';
import { getSensorProps } from '../../helpers/utils';
import { getMillSensor } from '../../attrs/sensorType';
import {
  IconbackgroundCircleStyle,
  TreeItemLabel,
  TreeItemWrapper,
  TreeList,
  TreeListParent
} from './CustomTreeViewStyles';

const getSensorNodeId = (machineId, sensorType, sensorIndex) => `${sensorType}_${machineId}_${sensorIndex}`;
const EMPTY_ARRAY = [];

const areTreeSelectionsEqual = (leftSelection, rightSelection) => {
  if (leftSelection.size !== rightSelection.size) {
    return false;
  }

  return Array.from(leftSelection.entries()).every(([machineId, leftMachineSelection]) => {
    const rightMachineSelection = rightSelection.get(machineId);

    if (!rightMachineSelection) {
      return false;
    }

    if (
      leftMachineSelection.productLine !== rightMachineSelection.productLine ||
      leftMachineSelection.machineID !== rightMachineSelection.machineID ||
      leftMachineSelection.machine !== rightMachineSelection.machine ||
      leftMachineSelection.childrenNames.length !== rightMachineSelection.childrenNames.length
    ) {
      return false;
    }

    return leftMachineSelection.childrenNames.every((leftChild, index) => {
      const rightChild = rightMachineSelection.childrenNames[index];

      return (
        leftChild.id === rightChild.id &&
        leftChild.name === rightChild.name &&
        leftChild.type === rightChild.type &&
        leftChild.is_custom === rightChild.is_custom &&
        leftChild.is_static === rightChild.is_static &&
        leftChild.icon === rightChild.icon
      );
    });
  });
};

const findNodeById = (nodes, id, parentId = null) => {
  const foundNode = nodes.find(node => node.id === id);
  if (foundNode) {
    return { ...foundNode, parentId };
  }

  return nodes.reduce((found, node) => {
    if (found || !node.children) {
      return found;
    }

    return findNodeById(node.children, id, node.id);
  }, null);
};

export const transformDataToTree = (rawData, editingSensors = []) =>
  rawData.map(productLine => ({
    id: productLine.name,
    name: productLine.name,
    children: (productLine.machines || []).map(machine => ({
      id: machine.id,
      name: machine.name,
      parentId: productLine.name,
      children: (machine.sensors || [])
        .map((sensor, index) => ({
          id: getSensorNodeId(machine.id, sensor.type, index),
          name: sensor?.custom_sensor_name || sensor?.static_sensor_name || `${sensor?.type}`,
          icon: sensor?.custom_sensor_icon || sensor?.static_sensor_icon || `${sensor?.type}`,
          is_custom: sensor.is_custom,
          is_static: sensor.is_static,
          type: sensor.type,
          productLine: productLine.name,
          machineID: machine.id,
          machine: machine.name,
          iconBackgroundStyle: 'rgba(239, 251, 241, 1)'
        }))
        .filter(sensorNode => !editingSensors.some(editingSensor => editingSensor.type === sensorNode.type))
    }))
  }));

export const mapSelectedItemsToTreeSelection = (selectedItemsParam = [], treeData = []) => {
  const selectedMap = new Map();

  if (!selectedItemsParam.length) {
    return selectedMap;
  }

  const sensorNodesByMachineAndType = treeData.reduce((lookup, productLineNode) => {
    productLineNode.children.forEach(machineNode => {
      machineNode.children.forEach(sensorNode => {
        const key = `${machineNode.id}::${sensorNode.type}`;
        const nodes = lookup.get(key) || [];
        nodes.push({
          ...sensorNode,
          productLine: productLineNode.name,
          machineID: machineNode.id,
          machine: machineNode.name
        });
        lookup.set(key, nodes);
      });
    });

    return lookup;
  }, new Map());

  const usedOccurrences = new Map();

  selectedItemsParam.forEach(selectedItem => {
    const machineId = selectedItem.machine_id ?? selectedItem.machineID;
    const lookupKey = `${machineId}::${selectedItem.type}`;
    const occurrenceIndex = usedOccurrences.get(lookupKey) || 0;
    const matchingNode = sensorNodesByMachineAndType.get(lookupKey)?.[occurrenceIndex];

    usedOccurrences.set(lookupKey, occurrenceIndex + 1);

    if (!selectedMap.has(machineId)) {
      selectedMap.set(machineId, {
        productLine:
          selectedItem?.product_line ??
          selectedItem?.production_line ??
          selectedItem?.productLine ??
          matchingNode?.productLine,
        machineID: machineId,
        machine: selectedItem?.machine ?? selectedItem?.machine_name ?? matchingNode?.machine,
        childrenNames: []
      });
    }

    selectedMap.get(machineId).childrenNames.push({
      id: selectedItem?.id ?? matchingNode?.id,
      name: selectedItem?.name ?? matchingNode?.name,
      type: selectedItem?.type ?? matchingNode?.type,
      is_custom: selectedItem?.is_custom ?? matchingNode?.is_custom,
      is_static: selectedItem?.is_static ?? matchingNode?.is_static,
      icon: selectedItem?.icon ?? selectedItem?.custom_icon_type ?? matchingNode?.icon
    });
  });

  return selectedMap;
};

const TreeItem = ({ node, handleSelection, selectedNodeIds, selectionCount, sensorsLength = 0 }) => {
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();

  let iconKey = node?.icon;
  let iconKeyName = node?.name;

  if (node.name.startsWith('mill_')) {
    const { name, mapValue } = getMillSensor(node.icon);
    iconKey = mapValue;
    iconKeyName = t(name);
  }

  const machineIconProps = {
    iconName: getSensorProps(iconKey).icon,
    status: SENSOR_STATUS_OK,
    style: {
      fontSize: '1.5rem'
    },
    viewBox: '0 0 32 32'
  };

  const isSelected = selectedNodeIds.has(node.id);
  const handleToggle = () => setIsOpen(prevIsOpen => !prevIsOpen);

  const handleCheck = event => {
    event.stopPropagation();
    handleSelection(node, event.target.checked);
  };

  const showName = () => (
    <div>
      {node.children &&
        (isOpen ? (
          <NotifyIcon iconName="chevronDown" fontSize="20px" />
        ) : (
          <NotifyIcon iconName="chevronRight" fontSize="default" />
        ))}
      {node.icon && (
        <IconbackgroundCircleStyle style={{ backgroundColor: node.iconBackgroundStyle }}>
          <MachineIcon {...machineIconProps} />
        </IconbackgroundCircleStyle>
      )}
      {node.children ? (
        <span
          style={{
            textTransform: 'capitalize',
            fontSize: '14px',
            color: '#333333',
            fontWeight: '500',
            verticalAlign: 'super'
          }}
        >
          {node.name}
        </span>
      ) : (
        <span style={{ fontSize: '14px', color: '#333333', fontWeight: '500' }}>{iconKeyName}</span>
      )}
    </div>
  );

  return (
    <TreeItemWrapper>
      <TreeItemLabel onClick={handleToggle} data-test-id="machines-button" key={node.id}>
        {!node.children && (
          <Checkbox
            checked={isSelected}
            data-test-id={`custom-tree-view-sensor-checkbox-${node.id}`}
            id={node.id}
            onChange={handleCheck}
            disabled={!isSelected && selectionCount >= 8 - sensorsLength}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>{showName()}</div>
      </TreeItemLabel>
      {isOpen && node.children && (
        <TreeList>
          {node.children.map(childNode => (
            <TreeItem
              key={childNode.id}
              node={childNode}
              handleSelection={handleSelection}
              selectedNodeIds={selectedNodeIds}
              selectionCount={selectionCount}
              sensorsLength={sensorsLength}
            />
          ))}
        </TreeList>
      )}
    </TreeItemWrapper>
  );
};

TreeItem.propTypes = {
  node: T.shape().isRequired,
  handleSelection: T.func.isRequired,
  selectedNodeIds: T.instanceOf(Set).isRequired,
  selectionCount: T.number.isRequired,
  sensorsLength: T.number
};

export const CustomTreeView = ({
  rawData,
  editingSensors = EMPTY_ARRAY,
  selectedItems: selectedItemsParam = EMPTY_ARRAY,
  onSelectionChange,
  sensorsLength = 0
}) => {
  const data = useMemo(() => transformDataToTree(rawData, editingSensors), [rawData, editingSensors]);
  const [selectedItems, setSelectedItems] = useState(new Map());
  const shouldEmitSelectionChangeRef = useRef(false);

  useEffect(() => {
    const nextSelectedItems = mapSelectedItemsToTreeSelection(selectedItemsParam, data);

    setSelectedItems(prevSelectedItems =>
      areTreeSelectionsEqual(prevSelectedItems, nextSelectedItems) ? prevSelectedItems : nextSelectedItems
    );
  }, [data, selectedItemsParam]);

  useEffect(() => {
    if (!shouldEmitSelectionChangeRef.current) {
      return;
    }

    shouldEmitSelectionChangeRef.current = false;
    onSelectionChange(Array.from(selectedItems.values()));
  }, [onSelectionChange, selectedItems]);

  const handleSelection = useCallback(
    (node, isChecked) => {
      shouldEmitSelectionChangeRef.current = true;

      setSelectedItems(prevSelectedItems => {
        const updatedSelectedItems = new Map(prevSelectedItems);
        const nodeWithParent = findNodeById(data, node.id);

        if (!nodeWithParent?.parentId) {
          return updatedSelectedItems;
        }

        const machineNode = findNodeById(data, nodeWithParent.parentId);

        if (!machineNode) {
          return updatedSelectedItems;
        }

        const machineSelection = updatedSelectedItems.get(machineNode.id) || {
          productLine: machineNode.parentId,
          machineID: machineNode.id,
          machine: machineNode.name,
          childrenNames: []
        };

        const childIndex = machineSelection.childrenNames.findIndex(child => child.id === nodeWithParent.id);

        if (isChecked && childIndex === -1) {
          machineSelection.childrenNames.push({
            id: nodeWithParent.id,
            name: nodeWithParent.name,
            type: nodeWithParent.type,
            is_custom: nodeWithParent.is_custom,
            is_static: nodeWithParent.is_static,
            icon: nodeWithParent.icon
          });
        }

        if (!isChecked && childIndex !== -1) {
          machineSelection.childrenNames.splice(childIndex, 1);
        }

        if (machineSelection.childrenNames.length > 0) {
          updatedSelectedItems.set(machineNode.id, machineSelection);
        } else {
          updatedSelectedItems.delete(machineNode.id);
        }

        return updatedSelectedItems;
      });
    },
    [data]
  );

  const selectionCount = useMemo(
    () =>
      Array.from(selectedItems.values()).reduce(
        (count, machineSelection) => count + machineSelection.childrenNames.length,
        0
      ),
    [selectedItems]
  );

  const selectedNodeIds = useMemo(
    () =>
      new Set(
        Array.from(selectedItems.values()).flatMap(machineSelection =>
          machineSelection.childrenNames.map(childSelection => childSelection.id)
        )
      ),
    [selectedItems]
  );

  return (
    <TreeListParent>
      {data.map(node => (
        <TreeItem
          key={node.id}
          node={node}
          selectedNodeIds={selectedNodeIds}
          handleSelection={handleSelection}
          selectionCount={selectionCount}
          sensorsLength={sensorsLength}
        />
      ))}
    </TreeListParent>
  );
};

CustomTreeView.propTypes = {
  rawData: T.arrayOf(
    T.shape({
      name: T.string.isRequired,
      machines: T.arrayOf(
        T.shape({
          id: T.string.isRequired,
          name: T.string.isRequired,
          sensors: T.arrayOf(T.shape())
        })
      )
    })
  ).isRequired,
  editingSensors: T.arrayOf(T.shape()),
  selectedItems: T.arrayOf(T.shape()),
  onSelectionChange: T.func.isRequired,
  sensorsLength: T.number
};
